// Ítem de Tiendanube en Mi marca (estilo nav, debajo de "Mis Modelos"):
//   • Si la marca NO tiene tienda conectada → botón "Conectar" + modal explicativo
//     (componente compartido `TiendaNubeConnectModal`, con explicación).
//   • Si YA está conectada → "conectada a [tienda] · Desconectar" (modal de confirmación).
// El estado lo trae `getTnStatus` (liviano). Connect/disconnect via `lib/tiendaNube`.

import React from "react";
import { createPortal } from "react-dom";
// `ChevronRight` es del rediseño del ítem (la fila estilo nav); el trío de
// `lib/tiendaNube` es el flujo de OAuth en ventana que trajo `principal` y reemplazó a
// `connectTn`. Van juntos: uno es cómo se ve la fila, el otro cómo conecta.
import { ChevronRight, Loader2, X } from "lucide-react";
import {
  getTnStatus,
  disconnectTn,
  connectTnEnVentana,
  abrirVentanaOAuth,
} from "../lib/tiendaNube";
import { TiendaNubeIcon } from "./TiendaNubeIcon";
import { TiendaNubeConnectModal } from "./TiendaNubeConnectModal";

export const TiendaNubeStatus: React.FC<{
  companyId: string | null;
  brandId: string | null;
  /** "card" (default, Mi marca desktop) o "boton" (Mi marca mobile, debajo del
   *  selector de marca). Ambas usan la MISMA lógica y modales; solo cambia el
   *  disparador.
   *
   *  Acá vivía `"icon"`: un botón redondo con sólo el logo, en el header mobile.
   *  Lo reemplazó `"boton"` en ADR-2026-08-04-tienda-nube-y-configuracion-con-label:
   *  un ícono suelto arriba a la derecha no decía qué hacía ni se leía como algo
   *  tocable. */
  variant?: "card" | "boton";
  /**
   * Vuelve CONTROLADO el modal de "Conectar" (para el deep-link
   * `/mi-marca?conectar=tiendanube` del banner de la home).
   *
   * Si se omite, el componente sigue manejando el modal con su estado interno
   * — o sea, ningún caller existente cambia de comportamiento.
   *
   * OJO: en Mi marca hay DOS instancias montadas a la vez (botón mobile y card
   * desktop, ocultas por CSS con `md:hidden` / `hidden md:block`). Pasarle esta
   * prop a las dos abriría DOS modales superpuestos. Pasársela a UNA sola
   * alcanza, porque el modal se renderiza con `createPortal` a `document.body`
   * y por lo tanto no le afecta que su contenedor esté oculto por CSS.
   */
  connectOpen?: boolean;
  onConnectOpenChange?: (open: boolean) => void;
}> = ({
  companyId,
  brandId,
  variant = "card",
  connectOpen,
  onConnectOpenChange,
}) => {
  const [status, setStatus] = React.useState<{
    connected: boolean;
    storeName: string | null;
    companyHasStores: boolean;
  } | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [showConnectInterno, setShowConnectInterno] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [connectError, setConnectError] = React.useState<string | null>(null);

  // Modo controlado vs. no controlado: si el padre pasa `connectOpen`, manda él.
  const controlado = connectOpen !== undefined;
  const showConnect = controlado ? connectOpen : showConnectInterno;
  const setShowConnect = React.useCallback(
    (open: boolean) => {
      if (controlado) onConnectOpenChange?.(open);
      else setShowConnectInterno(open);
    },
    [controlado, onConnectOpenChange],
  );

  React.useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setStatus(null);
      return;
    }
    (async () => {
      const s = await getTnStatus(brandId);
      if (!cancelled) setStatus(s);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, brandId]);

  if (!status) return null; // cargando / sin empresa

  const confirmDisconnect = async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    try {
      await disconnectTn(brandId);
      setStatus(await getTnStatus(brandId));
      setShowConfirm(false);
    } catch {
      /* si falla, queda como estaba */
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnect = async () => {
    if (connecting) return;
    // ⚠️ La ventana se abre ACÁ, sincrónicamente dentro del click, ANTES de
    // cualquier `await`. Si se espera a tener la authorizeUrl, Safari (y todos
    // los navegadores de iOS) la bloquean por pérdida de la activación del
    // gesto — ver ADR-2026-08-07-tiendanube-popup-safari. No mover esta línea
    // debajo de un await.
    const popup = abrirVentanaOAuth();
    setConnecting(true);
    setConnectError(null);
    try {
      await connectTnEnVentana(brandId, popup);
      setStatus(await getTnStatus(brandId)); // refrescar → queda conectada
      setShowConnect(false);
    } catch (e) {
      // Antes esto era un `catch` vacío: el popup bloqueado dejaba el botón
      // como si nada hubiera pasado y el usuario no se enteraba de nada.
      const msg = e instanceof Error ? e.message : "No se pudo conectar.";
      // Cerrar la ventana a propósito no es un error que haya que cantarle.
      setConnectError(msg === "Conexión cancelada." ? null : msg);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
      {variant === "boton" ? (
        // ── Fila completa (Mi marca mobile, debajo del selector de marca): la
        //    MISMA caja que el selector —`rounded-2xl`, borde y blanco— para que se
        //    lean como dos controles de la misma pila, y con el texto adentro. ──
        //
        //    Conectada cambia a informar + "Desconectar": "Conectar con Tienda Nube"
        //    sería una invitación a hacer lo que ya está hecho.
        status.connected ? (
          <div className="flex w-full items-center justify-between gap-2 rounded-2xl border border-black/[0.07] bg-white px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
              <div className="min-w-0">
                <div className="text-[14px] font-medium leading-tight text-brand-black/85">
                  Tienda Nube
                </div>
                <div className="truncate text-[12.5px] leading-[16px] text-black/45">
                  {status.storeName || "Conectada"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              className="shrink-0 text-[12.5px] font-medium text-neutral-500 transition-colors active:opacity-60"
            >
              Desconectar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowConnect(true)}
            className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.07] bg-white px-3 py-3 text-left transition-opacity active:opacity-70"
          >
            <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
            <span className="flex-1 text-[14px] font-medium text-brand-black/85">
              Conectar con Tienda Nube
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-black/30" />
          </button>
        )
      ) : status.connected ? (
        // ── Conectada: nombre de tienda + Desconectar ──
        <div className="mt-2 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-black/[0.07] bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-brand-black/80 leading-tight">
                Tiendanube
              </div>
              <div className="text-[12px] text-brand-black/45 truncate leading-tight">
                {status.storeName || "Conectada"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            Desconectar
          </button>
        </div>
      ) : (
        // ── No conectada: botón Conectar ──
        <div className="mt-2 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-black/[0.07] bg-white">
          <div className="flex items-center gap-2.5 min-w-0">
            <TiendaNubeIcon className="h-[18px] w-auto shrink-0 text-[#2c3357]" />
            <div className="text-[14px] font-medium text-brand-black/80 leading-tight">
              Tiendanube
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowConnect(true)}
            className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
          >
            Conectar
          </button>
        </div>
      )}

      {/* Modal Conectar (con explicación) — componente compartido con el import.
          El `&& !status.connected` cubre el deep-link: si alguien entra con
          `?conectar=tiendanube` a una marca que YA tiene tienda, no se le abre un
          modal para conectar algo que ya está conectado. Por los disparadores
          normales esto no puede pasar (el botón "Conectar" solo existe en la
          rama no-conectada). */}
      <TiendaNubeConnectModal
        open={showConnect && !status.connected}
        companyHasStores={status.companyHasStores}
        connecting={connecting}
        error={connectError}
        onCancel={() => {
          setConnectError(null);
          setShowConnect(false);
        }}
        onConnect={handleConnect}
      />

      {/* Modal: confirmar Desconexión */}
      {showConfirm &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[80]">
            <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
                  Desconectar Tiendanube
                </h3>
                <button
                  type="button"
                  onClick={() => !disconnecting && setShowConfirm(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <TiendaNubeIcon className="h-5 w-auto text-[#2c3357]" />
                </div>
                <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
                  ¿Desconectar tu Tiendanube de{" "}
                  <span className="font-medium text-neutral-800">
                    {status.storeName || "esta tienda"}
                  </span>{" "}
                  de esta marca? Vas a poder volver a conectarla cuando quieras.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={disconnecting}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-60"
                >
                  {disconnecting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {disconnecting ? "Desconectando…" : "Desconectar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TiendaNubeStatus;
